import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ShoppingCart, ArrowLeft, ZoomIn, Truck, Shield, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { cldPresets } from '../lib/cloudinary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveAbs = (u) => {
  if (!u) return '';
  return u.startsWith('http') ? u : `${BACKEND_URL}${u}`;
};

const ProductDetail = ({ addToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [zooming, setZooming] = useState(false);
  const [zoomXY, setZoomXY] = useState({ x: 50, y: 50 });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/products/${id}`);
        if (cancelled) return;
        setProduct(res.data);
        setActive(0);

        // Related: same category, exclude self
        const allRes = await axios.get(`${BACKEND_URL}/api/products`);
        if (cancelled) return;
        const others = (allRes.data || [])
          .filter((p) => p.id !== res.data.id && p.category === res.data.category)
          .slice(0, 4);
        setRelated(others);
      } catch (err) {
        toast.error('Product not found');
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    load();
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [id]);

  const images = useMemo(() => {
    if (!product) return [];
    return (product.images && product.images.length > 0)
      ? product.images
      : (product.image ? [product.image] : []);
  }, [product]);

  const overlays = useMemo(() => {
    if (!product) return [];
    if (product.imageOverlays && product.imageOverlays.length > 0) return product.imageOverlays;
    return product.imageOverlay ? [product.imageOverlay] : [];
  }, [product]);

  // schema.org Product JSON-LD — boosts Google Shopping / search visibility
  const jsonLd = useMemo(() => {
    if (!product) return null;
    const absImages = images.map(resolveAbs);
    const data = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: product.name,
      description: product.description,
      sku: product.id,
      category: product.category,
      image: absImages,
      brand: { '@type': 'Brand', name: 'BrainWave Architectural Projects' },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'ZAR',
        price: product.price?.toFixed(2),
        availability: product.inStock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        url: typeof window !== 'undefined' ? window.location.href : '',
      },
    };
    return JSON.stringify(data);
  }, [product, images]);

  // Inject / remove JSON-LD <script> in <head>
  useEffect(() => {
    if (!jsonLd) return undefined;
    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.text = jsonLd;
    tag.id = `pdp-jsonld-${id}`;
    document.head.appendChild(tag);
    return () => {
      try { document.head.removeChild(tag); } catch (e) { /* ignore */ }
    };
  }, [jsonLd, id]);

  const total = images.length;
  const go = (dir) => setActive((i) => (i + dir + total) % total);
  const goTo = (i) => setActive(i);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomXY({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-gray-500">Loading product…</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-24 flex flex-col items-center justify-center gap-4">
        <p className="text-gray-600">Product not available</p>
        <Link to="/shop">
          <Button variant="outline" style={{ borderColor: '#00CED1', color: '#00CED1' }}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Shop
          </Button>
        </Link>
      </div>
    );
  }

  const overlay = overlays[active] || '';
  const activeImage = resolveAbs(images[active] || '');

  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="pdp-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/shop"
          className="inline-flex items-center text-sm font-medium hover:underline mb-6"
          style={{ color: '#00CED1' }}
          data-testid="pdp-back-link"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image carousel + zoom */}
          <div>
            <div
              className="relative h-[28rem] lg:h-[32rem] overflow-hidden rounded-lg bg-gray-100 cursor-zoom-in"
              onMouseEnter={() => setZooming(true)}
              onMouseLeave={() => setZooming(false)}
              onMouseMove={handleMouseMove}
              data-testid="pdp-main-image"
            >
              {activeImage ? (
                <img
                  src={cldPresets.productCard ? cldPresets.productCard(activeImage) : activeImage}
                  alt={`${product.name} — image ${active + 1}`}
                  className="w-full h-full object-cover transition-transform duration-200 ease-out"
                  style={zooming
                    ? { transform: 'scale(2)', transformOrigin: `${zoomXY.x}% ${zoomXY.y}%` }
                    : { transform: 'scale(1)' }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">No image</div>
              )}

              {overlay && (
                <div className="absolute inset-x-0 bottom-0 px-4 py-3 bg-gradient-to-t from-black/70 to-transparent pointer-events-none">
                  <p
                    className="text-white font-semibold drop-shadow"
                    data-testid="pdp-overlay"
                  >
                    {overlay}
                  </p>
                </div>
              )}

              {/* Zoom hint */}
              {!zooming && total > 0 && (
                <div className="absolute top-3 right-3 bg-white/85 px-2 py-1 rounded text-xs flex items-center gap-1 pointer-events-none">
                  <ZoomIn className="h-3 w-3" style={{ color: '#00CED1' }} />
                  Hover to zoom
                </div>
              )}

              {total > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Previous image"
                    onClick={(e) => { e.stopPropagation(); go(-1); }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white shadow flex items-center justify-center hover:scale-110 transition"
                    data-testid="pdp-prev-btn"
                  >
                    <ChevronLeft className="h-5 w-5" style={{ color: '#00CED1' }} />
                  </button>
                  <button
                    type="button"
                    aria-label="Next image"
                    onClick={(e) => { e.stopPropagation(); go(1); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white shadow flex items-center justify-center hover:scale-110 transition"
                    data-testid="pdp-next-btn"
                  >
                    <ChevronRight className="h-5 w-5" style={{ color: '#00CED1' }} />
                  </button>
                </>
              )}

              {!product.inStock && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center pointer-events-none">
                  <Badge className="text-white bg-gray-800 text-base px-4 py-1">Out of Stock</Badge>
                </div>
              )}
            </div>

            {total > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="pdp-thumbs">
                {images.map((url, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goTo(i)}
                    className={`flex-shrink-0 w-20 h-20 rounded overflow-hidden border-2 transition ${
                      i === active ? 'border-[#00CED1]' : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                    data-testid={`pdp-thumb-${i}`}
                  >
                    <img
                      src={resolveAbs(url)}
                      alt={`Thumb ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right column: details */}
          <div>
            <Badge variant="outline" className="capitalize mb-3" style={{ borderColor: '#00CED1', color: '#00CED1' }}>
              {product.category}
            </Badge>
            <h1 className="text-4xl font-bold mb-3" data-testid="pdp-name">{product.name}</h1>
            <p className="text-3xl font-bold mb-6" style={{ color: '#00CED1' }} data-testid="pdp-price">
              R{product.price.toFixed(2)}
            </p>
            <p className="text-gray-700 leading-relaxed mb-6" data-testid="pdp-description">
              {product.description}
            </p>

            <div className="space-y-2 mb-6 text-sm">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4" style={{ color: '#00CED1' }} />
                <span>Free shipping on orders over R10,000</span>
              </div>
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" style={{ color: '#00CED1' }} />
                <span>30-day return policy</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" style={{ color: '#00CED1' }} />
                <span>Secure payment via Stripe</span>
              </div>
            </div>

            <Button
              onClick={() => {
                addToCart(product);
                toast.success(`${product.name} added to cart!`, {
                  duration: 2500,
                  style: { backgroundColor: '#00CED1', color: 'white' },
                });
              }}
              disabled={!product.inStock}
              size="lg"
              className="w-full sm:w-auto text-white font-semibold text-base px-8"
              style={{ backgroundColor: product.inStock ? '#00CED1' : '#9ca3af' }}
              data-testid="pdp-add-to-cart"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              {product.inStock ? 'Add to Cart' : 'Out of Stock'}
            </Button>

            <div className="mt-6 text-xs text-gray-500">SKU: {product.id}</div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold mb-6" data-testid="pdp-related-heading">You might also like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="pdp-related">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/shop/${p.id}`}
                  className="group block"
                  onClick={() => navigate(`/shop/${p.id}`)}
                  data-testid={`pdp-related-card-${p.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden rounded-lg bg-gray-100 mb-2">
                    {p.image && (
                      <img
                        src={resolveAbs(p.image)}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    )}
                  </div>
                  <h3 className="font-semibold text-sm truncate">{p.name}</h3>
                  <p className="text-sm font-bold" style={{ color: '#00CED1' }}>R{p.price.toFixed(2)}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
